import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CircleDollarSign, FolderKanban, Plus, ReceiptText, Sparkles, Trash2 } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import DataTable from '@/components/shared/DataTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SearchableSelect from '@/components/shared/SearchableSelect';
import PersianDatePickerFa from '@/components/shared/PersianDatePickerFa';
import { type ColumnDef } from '@tanstack/react-table';
import { accountingApi, type LookupProject } from '@/lib/api/accounting';
import { formatRial } from '@/lib/formatMoney';
import type { ExpenseTypeReportEntryRow, ExpenseTypeReportSummaryRow } from '@/lib/api/accounting';

type ExpenseTypeRow = {
  id: string;
  title: string;
  note?: string;
  active: boolean;
};

const ExpenseTypesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = React.useState<ExpenseTypeRow[]>([]);
  const [title, setTitle] = React.useState('');
  const [note, setNote] = React.useState('');
  const [projects, setProjects] = React.useState<LookupProject[]>([]);
  const [reportProjectId, setReportProjectId] = React.useState('');
  const [reportFromDate, setReportFromDate] = React.useState('');
  const [reportToDate, setReportToDate] = React.useState('');
  const [reportTypeId, setReportTypeId] = React.useState(() => searchParams.get('type')?.trim() ?? '');
  const [reportRows, setReportRows] = React.useState<ExpenseTypeReportSummaryRow[]>([]);
  const [entryRows, setEntryRows] = React.useState<ExpenseTypeReportEntryRow[]>([]);
  const [deleteTarget, setDeleteTarget] = React.useState<ExpenseTypeRow | null>(null);
  const [deleteError, setDeleteError] = React.useState('');

  React.useEffect(() => {
    const typeFromQuery = searchParams.get('type')?.trim() ?? '';
    setReportTypeId(typeFromQuery);
    if (typeFromQuery) {
      requestAnimationFrame(() => {
        document.getElementById('expense-types-report')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [searchParams]);

  const loadExpenseTypes = React.useCallback(async () => {
    const data = await accountingApi.expenseTypes();
    setRows(
      data.map((x) => ({
        id: String(x.id),
        title: x.title,
        note: x.note,
        active: Boolean(x.is_active),
      })),
    );
  }, []);

  React.useEffect(() => {
    Promise.all([accountingApi.lookups(), loadExpenseTypes()])
      .then(([res]) => setProjects(res.projects))
      .catch(() => setProjects([]));
  }, [loadExpenseTypes]);

  React.useEffect(() => {
    accountingApi
      .expenseTypesReport({
        project_id: reportProjectId ? Number(reportProjectId) : undefined,
        expense_type_id: reportTypeId ? Number(reportTypeId) : undefined,
        from_date: reportFromDate || undefined,
        to_date: reportToDate || undefined,
      })
      .then((res) => {
        setReportRows(res.summary || []);
        setEntryRows(res.entries || []);
      })
      .catch(() => {
        setReportRows([]);
        setEntryRows([]);
      });
  }, [reportFromDate, reportProjectId, reportToDate, reportTypeId]);

  const add = async () => {
    const t = title.trim();
    if (!t) return;
    await accountingApi.createExpenseType({
      title: t,
      note: note.trim() || undefined,
      is_active: true,
    });
    await loadExpenseTypes();
    setTitle('');
    setNote('');
  };

  const toggleActive = async (id: string) => {
    const current = rows.find((r) => r.id === id);
    if (!current) return;
    await accountingApi.updateExpenseType(Number(id), {
      title: current.title,
      note: current.note || '',
      is_active: !current.active,
    });
    await loadExpenseTypes();
  };

  const remove = async (id: string) => {
    await accountingApi.deleteExpenseType(Number(id));
    await loadExpenseTypes();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError('');
    try {
      await remove(deleteTarget.id);
      setDeleteTarget(null);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'حذف نوع هزینه با خطا مواجه شد.');
    }
  };

  const reportColumns = React.useMemo<ColumnDef<ExpenseTypeReportSummaryRow>[]>(
    () => [
      { accessorKey: 'typeTitle', header: 'نوع هزینه' },
      { accessorKey: 'count', header: 'تعداد سند' },
      {
        accessorKey: 'totalAmount',
        header: 'جمع پرداخت',
        cell: ({ row }) => <span className="font-mono">{formatRial(row.original.totalAmount)}</span>,
      },
    ],
    [],
  );

  const reportTotal = React.useMemo(
    () => reportRows.reduce((sum, r) => sum + r.totalAmount, 0),
    [reportRows],
  );

  const entryColumns = React.useMemo<ColumnDef<ExpenseTypeReportEntryRow>[]>(
    () => [
      { accessorKey: 'dateFa', header: 'تاریخ' },
      { accessorKey: 'beneficiary', header: 'طرف هزینه' },
      { accessorKey: 'projectLabel', header: 'پروژه' },
      { accessorKey: 'typeTitle', header: 'نوع هزینه' },
      { accessorKey: 'paymentMethod', header: 'روش پرداخت' },
      {
        accessorKey: 'amount',
        header: 'مبلغ',
        cell: ({ row }) => <span className="font-mono text-rose-700">{formatRial(row.original.amount)}</span>,
      },
      { accessorKey: 'description', header: 'شرح' },
    ],
    [],
  );

  const activeTypeCount = React.useMemo(() => rows.filter((r) => r.active).length, [rows]);
  const entriesTotalAmount = React.useMemo(
    () => entryRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [entryRows],
  );
  const avgTicket = entryRows.length > 0 ? Math.round(entriesTotalAmount / entryRows.length) : 0;
  const topType = reportRows[0];

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="نوع هزینه‌ها"
        description="طراحی حرفه‌ای برای دسته‌بندی هزینه‌ها، دید سریع روی اعداد کلیدی و دسترسی سریع به ثبت هزینه."
      />

      <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 via-white to-sky-50">
        <CardContent className="flex flex-col gap-4 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <p className="inline-flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
              <Sparkles className="h-3.5 w-3.5" />
              مرکز مدیریت هزینه
            </p>
            <h2 className="text-lg font-semibold text-slate-800">هم تعریف نوع هزینه، هم ثبت و رصد هزینه‌ها</h2>
            <p className="text-sm text-slate-600">
              از اینجا دسته‌بندی‌ها را مدیریت کن، بعد مستقیم برو سراغ ثبت هزینه در پروژه یا سند حسابداری.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild className="gap-2">
              <Link to="/projects">
                <FolderKanban className="h-4 w-4" />
                ورود به هزینه‌های پروژه
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="gap-2">
              <Link to="/accounting-documents/new">
                <ReceiptText className="h-4 w-4" />
                ثبت سند حسابداری
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Card className="border-indigo-100">
          <CardContent className="pt-5">
            <p className="text-xs text-slate-500">تعداد نوع هزینه</p>
            <p className="mt-1 text-2xl font-bold text-slate-800">{rows.length.toLocaleString('fa-IR')}</p>
            <p className="mt-2 text-xs text-emerald-600">فعال: {activeTypeCount.toLocaleString('fa-IR')}</p>
          </CardContent>
        </Card>
        <Card className="border-rose-100">
          <CardContent className="pt-5">
            <p className="text-xs text-slate-500">جمع هزینه ثبت‌شده (فیلتر فعلی)</p>
            <p className="mt-1 font-mono text-lg font-bold text-rose-700">{formatRial(entriesTotalAmount)}</p>
            <p className="mt-2 text-xs text-slate-500">تعداد ردیف: {entryRows.length.toLocaleString('fa-IR')}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-100">
          <CardContent className="pt-5">
            <p className="text-xs text-slate-500">میانگین هر هزینه</p>
            <p className="mt-1 font-mono text-lg font-bold text-amber-700">{formatRial(avgTicket)}</p>
            <p className="mt-2 text-xs text-slate-500">به ازای همین فیلترها محاسبه شده</p>
          </CardContent>
        </Card>
        <Card className="border-sky-100">
          <CardContent className="pt-5">
            <p className="text-xs text-slate-500">بیشترین نوع هزینه</p>
            <p className="mt-1 text-base font-semibold text-sky-800">{topType?.typeTitle ?? '—'}</p>
            <p className="mt-2 font-mono text-xs text-slate-500">{topType ? formatRial(topType.totalAmount) : '—'}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">افزودن نوع هزینه</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-12">
          <div className="md:col-span-4">
            <label className="mb-1 block text-xs text-slate-600">عنوان</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="مثلاً حمل و نقل" />
          </div>
          <div className="md:col-span-6">
            <label className="mb-1 block text-xs text-slate-600">توضیح</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="اختیاری" />
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button type="button" className="w-full gap-2" onClick={add}>
              <Plus className="h-4 w-4" />
              افزودن
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">لیست نوع هزینه‌ها (کارتی)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((r) => (
              <div
                key={r.id}
                className="relative flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-2"
                  onClick={() => setDeleteTarget(r)}
                >
                  <Trash2 className="h-4 w-4 text-rose-600" />
                </Button>
                <div>
                  <p className="font-medium text-slate-800">{r.title}</p>
                  <p className="mt-1 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                    {r.active ? 'فعال' : 'غیرفعال'}
                  </p>
                  <p className="text-xs text-slate-500">{r.note || '—'}</p>
                </div>
                <div className="flex items-center gap-2 pl-8">
                  <Button type="button" variant="outline" size="sm" onClick={() => toggleActive(r.id)}>
                    {r.active ? 'فعال' : 'غیرفعال'}
                  </Button>
                </div>
              </div>
            ))}
            {rows.length === 0 && (
              <p className="text-sm text-slate-500">هنوز نوع هزینه‌ای تعریف نشده است.</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card id="expense-types-report">
        <CardHeader>
          <CardTitle className="text-base">گزارش حرفه‌ای پرداخت بر اساس نوع هزینه</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
            <div className="md:col-span-4">
              <label className="mb-1 block text-xs text-slate-600">پروژه</label>
              <SearchableSelect
                value={reportProjectId}
                onChange={setReportProjectId}
                options={[
                  { value: '', label: 'همه پروژه‌ها' },
                  ...projects.map((p) => ({ value: String(p.id), label: p.label })),
                ]}
                placeholder="فیلتر پروژه"
              />
            </div>
            <div className="md:col-span-4">
              <label className="mb-1 block text-xs text-slate-600">نوع هزینه</label>
              <SearchableSelect
                value={reportTypeId}
                onChange={setReportTypeId}
                options={[
                  { value: '', label: 'همه نوع هزینه‌ها' },
                  ...rows.map((r) => ({ value: r.id, label: r.title })),
                ]}
                placeholder="فیلتر نوع هزینه"
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs text-slate-600">از تاریخ</label>
              <PersianDatePickerFa value={reportFromDate} onChange={setReportFromDate} />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs text-slate-600">تا تاریخ</label>
              <PersianDatePickerFa value={reportToDate} onChange={setReportToDate} />
            </div>
          </div>

          <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
            جمع کل پرداخت در این فیلتر: <span className="font-mono font-semibold">{formatRial(reportTotal)}</span>
          </div>

          <DataTable
            columns={reportColumns}
            data={reportRows}
            emptyMessage="برای این بازه/فیلتر داده‌ای پیدا نشد."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">هزینه‌های ثبت‌شده</CardTitle>
          <p className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600">
            <CircleDollarSign className="h-3.5 w-3.5" />
            آخرین وضعیت واقعی ثبت‌ها
          </p>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={entryColumns}
            data={entryRows}
            emptyMessage="در فیلترهای فعلی هزینه‌ای ثبت نشده است."
          />
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteError('');
          }
        }}
      >
        <DialogContent className="max-w-md [&>button]:left-4 [&>button]:right-auto">
          <DialogHeader>
            <DialogTitle>تایید حذف نوع هزینه</DialogTitle>
            <DialogDescription>
              آیا از حذف
              {' '}
              <span className="font-semibold text-slate-800">{deleteTarget?.title ?? ''}</span>
              {' '}
              مطمئن هستید؟ این عملیات قابل بازگشت نیست.
            </DialogDescription>
          </DialogHeader>
          {deleteError ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {deleteError}
            </div>
          ) : null}
          <div dir="ltr" className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteError('');
              }}
            >
              بستن
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDelete}>
              حذف شود
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpenseTypesPage;
